import { DeleteOutlined } from '@mui/icons-material';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import { RestApiKey } from 'graphql/generated/graphqlTypes';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import DrawerTemplate from 'v2/components/Drawer/DrawerTemplate';
import useDrawerContext from 'v2/components/Drawer/useDrawerContext';
import ModalTemplate from 'v2/components/Modal/ModalTemplate';
import useModalContext from 'v2/components/Modal/useModalContext';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import { API_KEY_MAX_NAME_LENGTH } from 'v2/constants';
import { formatDate } from 'v2/utils/dateTime';

const FORM_ID = 'edit-api-key-form';

interface FormValues {
  name: string;
}

interface Props {
  apiKey: RestApiKey;
  updateApiKey: (id: string, description: string) => Promise<boolean>;
  deleteApiKey: (id: string) => Promise<boolean>;
  existingNames: string[];
}

interface DeleteConfirmModalProps {
  onConfirm: () => Promise<boolean>;
}

const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({ onConfirm }) => {
  const { translation } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  const handleDelete = async () => {
    setError(undefined);
    setLoading(true);
    try {
      const success = await onConfirm();
      if (!success) setError(translation.api_key_error_deleting);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalTemplate
      title={translation.delete_api_key}
      primaryAction={{
        label: translation.delete,
        onClick: handleDelete,
        loading,
        dangerzone: true,
        id: 'delete-api-key-button'
      }}
      secondaryAction={{ label: translation.cancel, id: 'cancel-delete-api-key-button' }}
    >
      <Stack spacing={1}>
        <Typography>{translation.delete_api_key_confirmation}</Typography>
        <Typography variant="body2" color="error">
          {translation.this_action_is_irreversible}
        </Typography>
        {error && (
          <Alert severity="error" variant="filled" onClose={() => setError(undefined)}>
            {error}
          </Alert>
        )}
      </Stack>
    </ModalTemplate>
  );
};

const EditApiKeyDrawer: React.FC<Props> = ({ apiKey, updateApiKey, deleteApiKey, existingNames }) => {
  const { translation } = useTranslation();
  const { showSnackbar } = useSnackbarContext();
  const { openModal, closeModal } = useModalContext();
  const { closeDrawer } = useDrawerContext();
  const [error, setError] = useState<string>();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<FormValues>({
    mode: 'onTouched',
    defaultValues: { name: apiKey.description }
  });

  const onSubmit = async (values: FormValues) => {
    setError(undefined);
    const success = await updateApiKey(apiKey.id, values.name);
    if (!success) {
      setError(translation.api_key_error_updating);
      return;
    }
    showSnackbar(translation.saved_changes, { severity: 'success' });
    closeDrawer();
  };

  const handleConfirmDelete = async (): Promise<boolean> => {
    const success = await deleteApiKey(apiKey.id);
    if (success) {
      showSnackbar(translation.api_key_deleted, { severity: 'success' });
      closeModal();
      closeDrawer();
    }
    return success;
  };

  const handleDeleteClick = () => {
    openModal(<DeleteConfirmModal onConfirm={handleConfirmDelete} />);
  };

  return (
    <DrawerTemplate
      loading={isSubmitting}
      id="drawer-edit-api-key"
      title={translation.edit_api_key}
      primaryAction={{
        label: translation.save_changes,
        onClick: handleSubmit(onSubmit),
        form: FORM_ID,
        loading: isSubmitting,
        id: 'submit-button'
      }}
      error={error}
      onDismissError={() => setError(undefined)}
    >
      <Stack sx={{ flex: 1 }}>
        <Stack spacing={3} component="form" id={FORM_ID} onSubmit={handleSubmit(onSubmit)} noValidate>
          <TextField
            {...register('name', {
              required: translation.name_cannot_be_empty,
              maxLength: {
                value: API_KEY_MAX_NAME_LENGTH,
                message: translation.api_key_name_max_length_error.replace('{{count}}', String(API_KEY_MAX_NAME_LENGTH))
              },
              validate: (value) => !existingNames.includes(value) || translation.name_must_be_unique
            })}
            id="drawer-input-name"
            label={translation.name}
            error={!!errors.name}
            helperText={errors.name?.message}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            id="drawer-input-api-key-reference"
            label={translation.api_key_reference}
            value={apiKey.apiKey}
            disabled
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ input: { fontFamily: (theme) => theme.custom.font.mono } }}
          />
          <TextField
            id="drawer-input-creation-date"
            label={translation.creation_date}
            value={formatDate(apiKey.createdAt)}
            disabled
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Stack>
        <Box sx={{ flexGrow: 1 }} />
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 3 }}>
          <Button
            id="drawer-delete-api-key"
            variant="outlined"
            color="error"
            onClick={handleDeleteClick}
            type="button"
            startIcon={<DeleteOutlined color="error" />}
          >
            {translation.delete_api_key}
          </Button>
        </Box>
      </Stack>
    </DrawerTemplate>
  );
};

export default EditApiKeyDrawer;
